import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/lib/api";

export interface ApiResource<T> {
  data: T | undefined;
  loading: boolean;
  error: ApiError | null;
  recargar: () => void;
}

/**
 * GET con loading/error. Descarta respuestas viejas (si cambia la ruta o se recarga, la anterior se aborta).
 * `ruta = null` no pide nada (útil mientras falta un parámetro).
 */
export function useApiResource<T>(ruta: string | null): ApiResource<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(ruta !== null);
  const [error, setError] = useState<ApiError | null>(null);
  const [version, setVersion] = useState(0);
  const controlador = useRef<AbortController | null>(null);

  useEffect(() => {
    if (ruta === null) {
      setLoading(false);
      return;
    }
    controlador.current?.abort();
    const actual = new AbortController();
    controlador.current = actual;
    setLoading(true);
    setError(null);
    api
      .get<T>(ruta, actual.signal)
      .then((d) => {
        if (!actual.signal.aborted) setData(d);
      })
      .catch((e: unknown) => {
        if (actual.signal.aborted) return;
        setError(e instanceof ApiError ? e : new ApiError(0, "Error inesperado"));
      })
      .finally(() => {
        if (!actual.signal.aborted) setLoading(false);
      });
    return () => actual.abort();
  }, [ruta, version]);

  const recargar = useCallback(() => setVersion((v) => v + 1), []);
  return { data, loading, error, recargar };
}
